import "./TicketCalendarCss.css";
import React, { useEffect, useRef, useState } from 'react';
import dayjs from "dayjs";
import FullCalendar from '@fullcalendar/react';
import ToastMessage from "../../../common/ToastMessage";
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import useTickets from "../../../store/ReactQueries/tickets/useTicket";
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import { useNavigate } from "react-router-dom";
import { EndPoints } from "../../../utils/apiEndpoints";
import { darkModeBackgroud } from "../../../utils/constants";
import { useDarkModeStore } from "../../../store/useDarkModeStore";
import { useLanguageStore } from "../../../store/useLanguageStore";
import { DatesSetArg, EventClickArg, SlotLabelContentArg } from "@fullcalendar/core";
import { CalendarInterface, CalendarResourseInterface } from "../../../models/TicketModule/Calendar";

export const TicketCalendar = () => {
    const navigation = useNavigate();
    const calendarRef = useRef(null);

    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();
    const { data: tickets, isLoading: ticketLoading, error } = useTickets(0, 0);

    const [loading, setLoading] = useState<boolean>(true);
    const [resources, setResources] = useState<CalendarResourseInterface[]>([]);
    const [events, setEvents] = useState<CalendarInterface[]>([]);

    useEffect(() => {
        if (error) ToastMessage.Failed(error.message);
    }, [error]);

    const populateTickets = () => {
        let upResources: CalendarResourseInterface[] = [];
        let upEvents: CalendarInterface[] = [];
        if (tickets) {
            tickets.forEach((ticket) => {
                const assignToId = ticket.assignedTo?.id;
                if (assignToId) {
                    const existingResource = upResources.find(resource => resource.id === assignToId);

                    if (existingResource) existingResource.items += 1;
                    else {
                        upResources.push({
                            id: assignToId,
                            title: `${ticket.assignedTo?.firstname} ${ticket.assignedTo?.lastname}`,
                            items: 1
                        });
                    }
                    upEvents.push({
                        id: ticket.id ?? '',
                        resourceId: assignToId,
                        title: ticket.title ?? 'No Title',
                        start: ticket.createdDate ? dayjs(ticket.createdDate).hour(0).minute(0).toISOString() : '',
                        end: ticket.dueDate ? dayjs(ticket.dueDate).hour(23).minute(59).toISOString() : '',
                        description: ticket.description ?? 'No Description',
                        isCompleted: ticket.status ? ticket.status?.isCompleted : false,
                    });
                }
            });
        }
        setResources(upResources);
        setEvents(upEvents);
    };

    useEffect(() => {
        setLoading(true);

        populateTickets();

        setTimeout(() => {
            setLoading(false);
        }, 1000);
    }, [tickets, language])

    const styleCss = { width: 12, height: 12, marginBottom: .1 };

    const tHeadColumn = (arg: SlotLabelContentArg) => {
        const { date } = arg;
        const formatDate = dayjs(date);
        return <div className={`d-flex flex-column gap-1 ${darkMode && 'text-white'}`}>
            <span>{formatDate.format('dddd')} {formatDate.format('DD / MM')}</span>
        </div>
    };

    const tLeftRow = (title: string, items: number) => {
        return (
            <div className="d-flex flex-row gap-1 align-items-center position-relative" style={{ fontSize: 12, color: darkMode ? 'white' : '' }}>
                <AccountCircleRoundedIcon className="ms-2" sx={{ width: 30, height: 30 }} />
                <div className="d-flex flex-row align-items-center gap-1 justify-content-center">
                    <span className="fw-bold" style={{ width: 'fit-content' }}>{title}</span>
                    <div className="d-flex flex-row gap-1 align-items-center position-absolute" style={{ top: 5, right: 5 }}>
                        <ContentCopyRoundedIcon sx={styleCss} />
                        <span>{items}</span>
                    </div>
                </div>
            </div>
        );
    };

    const eventCards = (eventInfo: any) => {
        const { event } = eventInfo;
        const { extendedProps } = event;
        return (
            <div className="custom-event-div" title={extendedProps?.description}>
                <div className="d-flex flex-column justify-content-center p-2">
                    <strong className={`${extendedProps?.isCompleted && 'text-decoration-line-through'}`} style={{ color: 'black' }}>{event.title}</strong>
                    <div className={`text-muted text-truncate ${extendedProps?.isCompleted && 'text-decoration-line-through'}`} style={{ maxWidth: '300px' }}>{extendedProps?.description}</div>
                </div>
            </div>
        );
    };

    const updateTitle = (arg: DatesSetArg) => {
        const { start, end } = arg;
        const formattedStart = dayjs(start).format('DD MMMM');
        const formattedEnd = dayjs(end).format('DD MMMM YYYY');

        const newTitle = `${formattedStart} - ${formattedEnd}`;

        const toolbarTitle = document.querySelector('.fc-toolbar-title') as HTMLElement;
        toolbarTitle.innerText = newTitle;
    };

    return (
        <React.Fragment>
            {
                ticketLoading || loading
                    ? <SkeletonTheme baseColor={darkMode ? darkModeBackgroud : "#ededf2"} highlightColor={`${darkMode ? darkModeBackgroud : '#eee'}`}>
                        <div className="d-flex flex-row align-items-center justify-content-between mt-3">
                            <Skeleton style={{ width: 65, height: 38 }} />
                            <Skeleton style={{ width: 265, height: 30 }} />
                            <div className="d-flex flex-row">
                                {[1, 2, 3, 4, 5].map(item => <Skeleton key={item} className={`${item === 4 ? 'ms-2' : ''}`} style={{ width: 55, height: 38, borderRadius: 0 }} />)}
                            </div>
                        </div>
                        <div className="mt-3">
                            <table>
                                <thead>
                                    <tr>
                                        {[1, 2, 3, 4, 5, 6, 7, 8].map(item => <th key={item}>  <Skeleton style={{ width: item === 1 ? 200 : 185, height: 55 }} /></th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[1, 2, 3, 4].map(item =>
                                        <tr key={item}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <td key={i}> <Skeleton style={{ width: i === 1 ? 200 : 185, height: 55 }} /></td>)}
                                        </tr>)}
                                </tbody>
                            </table>
                        </div>
                    </SkeletonTheme>
                    : <FullCalendar
                        firstDay={1}
                        height="auto"
                        events={events}
                        ref={calendarRef}
                        resources={resources}
                        slotDuration="24:00:00"
                        resourceAreaWidth={200}
                        eventContent={eventCards}
                        eventClassNames='cursor-pointer'
                        initialView="resourceTimelineWeek"
                        plugins={[resourceTimelinePlugin]}
                        datesSet={(arg) => updateTitle(arg)}
                        initialDate={dayjs().format("YYYY-MM-DD")}
                        slotLabelContent={(arg) => tHeadColumn(arg)}
                        resourceAreaHeaderContent={language['AssignedTo']}
                        viewClassNames={darkMode ? 'dark-mode-bg-color' : 'bg-white'}
                        slotLabelClassNames={`fw-normal ${darkMode ? 'dark-mode-input border-color-dark-mode' : ''}`}
                        resourceLabelContent={(arg) => tLeftRow(arg.resource?.title, arg.resource.extendedProps.items)}
                        resourceAreaHeaderClassNames={`fw-normal ${darkMode ? 'dark-mode-input border-color-dark-mode' : ''}`}
                        eventClick={(arg: EventClickArg) => navigation(`${EndPoints.TICKETS}/edit/${arg.event._def.publicId}`)}
                        slotLabelFormat={{
                            hour: '2-digit',
                            minute: '2-digit'
                        }}
                        headerToolbar={{
                            left: 'today',
                            center: 'title',
                            right: 'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth prev,next'
                        }}
                        buttonText={{
                            today: `${language['Today']} 📆`,
                            day: language['Day'],
                            week: language['Week'],
                            month: language['Month'],
                        }}
                    />
            }
        </React.Fragment >
    );
}

export default TicketCalendar;
